import * as React from "react"
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Spinner,
  Flex,
  Button,
  Image,
  ModalProps,
  Icon,
  Input,
  Stack,
  Center,
  HStack,
} from "@chakra-ui/react"
import { matchSorter } from "match-sorter"
import { useQuery } from "react-query"
import { FiCheck } from "react-icons/fi"
import { useOrgs } from "./providers/OrgProvider"
import { useFeatFlags } from "./providers/FeatFlagProvider"
import { useMe } from "./providers/MeProvider"
import { useToast } from "../lib/hooks/useToast"
import { OrgsResponse, OrgEntity } from "../lib/types/org"
import { hierarchySorter } from "../lib/helpers/utils"
import { ORGS_IS_NOA } from "../lib/static/organisation"
import { useMapBBox } from "./providers/MapBBoxProvider"

export const OrgSwitcher = (props: Omit<ModalProps, "children">) => {
  const me = useMe()
  const toast = useToast()
  const { currentOrgs, setOrgs } = useOrgs()
  const { multi_org } = useFeatFlags()
  const { setCurrentBBox } = useMapBBox()

  const [search, setSearch] = React.useState("")
  const [selectedOrgs, setSelectedOrgs] = React.useState(currentOrgs)
  const { isLoading, data } = useQuery<OrgsResponse, string>(
    `organizations/?user=${me.uuid}&page_size=999&page=1`,
    {
      staleTime: Infinity,
    },
  )
  const orgs = data?.results || []
  const beforeHierarchy = orgs.sort((a, b) => (a.name > b.name ? 1 : -1))
  const organizations = matchSorter(beforeHierarchy, search, { keys: ["name"] })
  const filteredOrgs = hierarchySorter(organizations)

  const handleClose = React.useCallback(() => {
    setSearch("")
    props.onClose()
  }, [setSearch, props])

  const handleSave = React.useCallback(() => {
    // Could be moved/called at the end of onToggle
    if (selectedOrgs.length > 0) {
      setCurrentBBox(undefined)
      setOrgs(selectedOrgs)
      toast({ status: "success", description: "Organizations updated!" })
      handleClose()
    } else {
      toast({ status: "warning", description: "At least 1 organisation must be selected!" })
    }
  }, [selectedOrgs, toast, handleClose, setOrgs, setCurrentBBox])

  const handleSelectSingleOrg = React.useCallback(
    (org: OrgEntity) => {
      setCurrentBBox(undefined)
      setOrgs([org])
      toast({ status: "success", description: "Organisation updated!" })
      handleClose()
    },
    [handleClose, setOrgs, toast, setCurrentBBox],
  )

  const handleReset = React.useCallback(() => {
    const defaultOrg = organizations.filter((e) => e.hierarchyLevel === 0)[0]
    setSelectedOrgs(defaultOrg ? [defaultOrg] : [organizations[0]])
    setCurrentBBox(undefined)
    setOrgs(defaultOrg ? [defaultOrg] : [organizations[0]])
    handleClose()
  }, [setSelectedOrgs, setOrgs, handleClose, organizations, setCurrentBBox])

  const getChildOrgs = React.useCallback(
    (org: OrgEntity): { selected: OrgEntity[]; unselected: OrgEntity[] } => {
      const lvl1 = organizations.filter((e) => e.parent === org.uuid)
      let lvl2: OrgEntity[] = []
      let lvl3: OrgEntity[] = []
      lvl1.forEach((lvl1Org) => {
        const lvl2orgs = organizations.filter((e) => e.parent === lvl1Org.uuid)
        lvl2 = lvl2.concat(lvl2orgs)
        lvl2orgs.forEach((lvl2org) => {
          const lvl3orgs = organizations.filter((e) => e.parent === lvl2org.uuid)
          lvl3 = lvl3.concat(lvl3orgs)
        })
      })
      const children = lvl1.concat(lvl2).concat(lvl3)
      const selected = children.filter((c) => selectedOrgs.find((s) => c.uuid === s.uuid))
      const unselected = children.filter((c) => !selectedOrgs.find((s) => c.uuid === s.uuid))
      return {
        selected,
        unselected,
      }
    },
    [organizations, selectedOrgs],
  )

  const handleToggle = React.useCallback(
    (org: OrgEntity) => {
      if (multi_org.visible) {
        const alreadySelected = selectedOrgs.find(({ uuid }) => uuid === org.uuid)
        const children = getChildOrgs(org)
        if (alreadySelected) {
          if (selectedOrgs.length > 1 && ORGS_IS_NOA([org])) {
            // remove noa org without touching children
            setSelectedOrgs((selected) => selected.filter((s) => s.uuid !== org.uuid))
            return
          }
          if (children.unselected.length === 0) {
            // select org only
            if (selectedOrgs.length <= 1 + children.unselected.length + children.selected.length)
              setSelectedOrgs([org])
            // unselect org and children
            else
              setSelectedOrgs((selected) =>
                selected.filter(
                  (s) => !children.selected.find((c) => c.uuid === s.uuid) && s.uuid !== org.uuid,
                ),
              )
          } else {
            // avoid all chidren selection for noa org (performance issue)
            if (ORGS_IS_NOA([org])) return
            // select all children
            setSelectedOrgs((selected) => [...selected, ...children.unselected])
          }
        } else {
          if (!ORGS_IS_NOA([org])) {
            // select current org and remove noa org
            setSelectedOrgs((selected) => {
              const toSelect = selected.filter((org) => !ORGS_IS_NOA([org]))
              return [...toSelect, org]
            })
          } else setSelectedOrgs((selected) => [...selected, org])
        }
      } else {
        handleSelectSingleOrg(org)
      }
    },
    [selectedOrgs, multi_org, handleSelectSingleOrg, getChildOrgs],
  )

  return (
    <Modal {...props} isCentered scrollBehavior="inside">
      <ModalOverlay zIndex={100}>
        <ModalContent>
          <ModalHeader>
            {multi_org.visible ? `Change Organizations · ${selectedOrgs.length}` : "Change Organization"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody h={550} pb="100px">
            <Input
              mb={4}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
            />
            {isLoading ? (
              <Center>
                <Spinner />
              </Center>
            ) : (
              <Stack>
                {filteredOrgs.map((org: OrgEntity) => (
                  <OrgItem
                    key={org.uuid}
                    org={org}
                    isSelected={!!selectedOrgs.find(({ uuid }) => uuid === org.uuid)}
                    onToggle={() => handleToggle(org)}
                  />
                ))}
              </Stack>
            )}

            {multi_org.visible && (
              <Flex
                align="center"
                justify="space-between"
                pos="absolute"
                borderRadius="md"
                bottom={0}
                right={0}
                left={0}
                p={4}
                bg="white"
              >
                {selectedOrgs.length > 1 ? (
                  <Button variant="ghost" onClick={handleReset}>
                    Reset
                  </Button>
                ) : (
                  <Flex />
                )}
                <HStack>
                  <Button variant="ghost" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button colorScheme="green" onClick={handleSave}>
                    Update
                  </Button>
                </HStack>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}

interface OrgItemProps {
  org: OrgEntity
  isSelected: boolean
  onToggle: () => void
}

const OrgItem = ({ org, isSelected, onToggle }: OrgItemProps) => {
  return (
    <Button variant="ghost" tabIndex={0} w="100%" pr={2} pl={0} py={0} onClick={onToggle}>
      <Flex w="100%" justify="space-between" align="center">
        <Flex justify="flex-start" align="center" pl={org.hierarchyLevel ? org.hierarchyLevel * 10 : 0}>
          <Image src={org.icon} mr={2} boxSize="34px" objectFit="contain" />
          {org.name}
        </Flex>
        {isSelected && <Icon as={FiCheck} color="green.500" boxSize="30px" />}
      </Flex>
    </Button>
  )
}
